import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check, Clock, FileDown, Zap } from "lucide-react";
import { Brand } from "@/components/Brand";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_github-converter-1/artifacts/5slfahq8_WIv2T.jpg";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
};

export default function Landing() {
  return (
    <div className="min-h-screen bg-white text-[#1B1B3A]">
      {/* Nav */}
      <header className="absolute top-0 left-0 right-0 z-30">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 h-24 flex items-center justify-between">
          <Brand variant="dark" />
          <nav className="flex items-center gap-3 sm:gap-6">
            <div className="hidden sm:flex flex-col items-end leading-tight gap-1">
              <a
                href="https://techport.nl"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center font-condensed font-semibold uppercase text-sm tracking-wider text-white/90 hover:text-[#3FCBA6] transition-colors"
                data-testid="nav-training-link"
              >
                Naar techport.nl
              </a>
              <a
                href="https://calendar.app.google/DJx7rsSrN7YpGCA96"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center font-condensed font-semibold uppercase text-sm tracking-wider text-[#3FCBA6] hover:text-white transition-colors"
                data-testid="nav-appointment-link"
              >
                Direct een afspraak maken
              </a>
            </div>
            <Link to="/intake" className="tp-btn tp-btn--mint" data-testid="nav-start-link">
              Start intake
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero — full bleed photo + dark overlay + big Anton headline */}
      <section className="relative min-h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
        </div>

        <div className="relative z-10 max-w-[1500px] mx-auto px-6 lg:px-12 pb-24 lg:pb-32 pt-40 w-full grid grid-cols-12 gap-8">
          <motion.div
            className="col-span-12 lg:col-span-9"
            {...fadeUp}
            transition={{ duration: 0.7, ease: "easeOut" }}
          >
            <div className="font-condensed uppercase tracking-[0.3em] text-[13px] text-[#3FCBA6] font-semibold mb-6">
              · Voor CEO, CFO &amp; bestuursvoorzitters · 3 vragen, 7 minuten ·
            </div>
            <h1 className="font-display text-white text-[56px] sm:text-[88px] lg:text-[140px]">
              Drie vragen.<br />
              <span className="italic-em" style={{ fontStyle: "normal" }}>Eén AI</span><br />
              Business Case.
            </h1>

            <p className="mt-10 max-w-2xl text-lg lg:text-xl text-white/85 leading-relaxed">
              Geen ronkende strategie-deck van 60 pagina's. Beantwoord drie scherpe vragen over jullie
              grootste kostenposten en pijnpunten — en krijg <em className="italic-em" style={{ fontStyle: "normal" }}>3 besparingsvoorstellen</em>.
            </p>

            <div className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/intake" className="tp-btn tp-btn--mint" data-testid="start-intake-button">
                Start intake
              </Link>
              <a
                href="https://calendar.app.google/DJx7rsSrN7YpGCA96"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-3 px-5 py-3.5 font-display uppercase text-[15px] tracking-[0.05em] text-white border-2 border-white/60 hover:bg-white hover:text-[#1B1B3A] transition-colors"
                data-testid="hero-appointment-button"
              >
                Direct een afspraak maken
                <span aria-hidden>▸</span>
              </a>
              <span className="font-condensed uppercase text-sm tracking-[0.15em] text-white/60">
                7 min · Direct PDF · Voor directies
              </span>
            </div>
          </motion.div>
        </div>

        {/* Bottom strip — Techport-style numbers */}
        <div className="absolute bottom-0 left-0 right-0 z-10 hidden lg:block">
          <div className="bg-[#1B1B3A] text-white">
            <div className="max-w-[1500px] mx-auto px-12 py-6 grid grid-cols-4 divide-x divide-white/15">
              {[
                { k: "3", v: "vragen" },
                { k: "3", v: "business cases" },
                { k: "90", v: "dagen resultaat" },
                { k: "€/FTE", v: "geschatte impact" },
              ].map((s) => (
                <div key={s.v} className="px-6 first:pl-0 last:pr-0 flex items-baseline gap-4">
                  <span className="font-display text-5xl text-[#3FCBA6]">{s.k}</span>
                  <span className="font-condensed uppercase text-xs tracking-[0.2em] text-white/60">{s.v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission strip — Techport-style green block */}
      <section className="mint-bg">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-3">
            <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#1B1B3A]/70 font-semibold">
              · onze missie ·
            </div>
          </div>
          <div className="col-span-12 lg:col-span-9">
            <h2 className="font-display text-[44px] sm:text-6xl lg:text-[88px] text-[#1B1B3A]">
              Waar zit de winst?<br />
              We rekenen het<br />
              <em className="italic-em" style={{ color: "#1B1B3A", fontStyle: "normal" }}>voor je uit.</em>
            </h2>
            <p className="mt-10 max-w-2xl text-[#1B1B3A] text-lg lg:text-xl leading-relaxed font-medium">
              We doorlopen samen drie scherpe vragen over jullie organisatie, kostenstructuur en pijnpunten.
              Op basis daarvan krijg je drie AI business cases — concreet voor jullie sector, met
              geschatte besparing in € of FTE per jaar. Direct bruikbaar voor MT, RvT of aandeelhouders.
            </p>
            <div className="mt-12 flex flex-wrap gap-3">
              <Link to="/intake" className="tp-btn" data-testid="start-intake-button-mission">
                Ja, help mij kostenbesparen
              </Link>
              <a href="https://techport.nl" target="_blank" rel="noopener noreferrer" className="tp-btn tp-btn--ghost">
                Word partner
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How it works — 3 steps */}
      <section className="bg-white">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-24">
          <div className="grid grid-cols-12 gap-8 mb-16">
            <div className="col-span-12 lg:col-span-4">
              <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#6B6B85] font-semibold mb-4">
                · hoe het werkt ·
              </div>
              <h2 className="font-display text-5xl lg:text-7xl text-[#1B1B3A]">
                drie stappen.<br />
                geen ruis.
              </h2>
            </div>
            <div className="col-span-12 lg:col-span-7 lg:col-start-6 flex items-end">
              <p className="text-lg lg:text-xl text-[#1B1B3A]/75 leading-relaxed">
                Praat met de intake-agent. Krijg je rapport. Begin morgen met één concrete use case —
                niet met een ver ideaalbeeld over twee jaar.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                n: "01",
                t: "Strategische intake",
                d: "Drie brede vragen over rol, organisatie, kostenstructuur, AI-volwassenheid en blokkades. Eén voor één, op directieniveau.",
              },
              {
                n: "02",
                t: "AI Business Case",
                d: "Drie concrete use cases voor jullie organisatie — 1 quick win + 2 strategische — met geschatte besparing in € of FTE per jaar.",
              },
              {
                n: "03",
                t: "Binnen 90 dagen resultaat",
                d: "Helder PDF-stappenplan. Maand 1: assessment + quick win. Maand 2: pilot. Maand 3: schaal & verankering.",
              },
            ].map((step, i) => (
              <motion.div
                key={step.n}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="border-2 border-[#1B1B3A] p-8 lg:p-10 flex flex-col hover:bg-[#3FCBA6] transition-colors group"
                data-testid={`step-card-${step.n}`}
              >
                <div className="font-display text-[120px] text-[#1B1B3A] mb-4 leading-none">
                  {step.n}
                </div>
                <h3 className="font-display text-3xl lg:text-4xl text-[#1B1B3A] mb-4">
                  {step.t}
                </h3>
                <p className="text-[#1B1B3A]/80 leading-relaxed text-base">{step.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What you get — list with mint accent */}
      <section className="bg-[#F8F8FA]">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-24 grid grid-cols-12 gap-12">
          <div className="col-span-12 lg:col-span-5">
            <div className="font-condensed uppercase tracking-[0.25em] text-sm text-[#6B6B85] font-semibold mb-4">
              · wat je krijgt ·
            </div>
            <h2 className="font-display text-5xl lg:text-7xl text-[#1B1B3A] mb-8">
              Drie AI<br />
              <em className="italic-em" style={{ fontStyle: "normal" }}>business cases</em>.
            </h2>
            <p className="text-[#1B1B3A]/75 text-lg leading-relaxed max-w-md mb-10">
              Geen Gartner-quadrant. Geen "transformatie-roadmap". Wij kijken naar wat jij
              letterlijk zegt over jullie pijn — en vertalen dat naar drie cases met euro's en FTE eraan.
            </p>
            <Link to="/intake" className="tp-btn" data-testid="start-intake-button-bottom">
              Ja, ik wil direct aan de slag
            </Link>
          </div>

          <div className="col-span-12 lg:col-span-7 space-y-3">
            {[
              { t: "Profiel + kernobservatie", d: "Waar zit de grootste financiële of operationele winst" },
              { t: "Drie AI business cases", d: "Quick win + 2 strategisch, gekoppeld aan jullie context" },
              { t: "Geschatte besparing per jaar", d: "In € of FTE — gebaseerd op jullie bedrijfsomvang" },
              { t: "Zelf morgen aan de slag", d: "En na 3 maanden de resultaten meten — concreet plan per maand" },
              { t: "Risico's & directie-acties", d: "Compliance, IT-capaciteit, weerstand, budget — concreet" },
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="flex items-start gap-5 bg-white border-2 border-[#1B1B3A] p-6 hover:bg-[#3FCBA6] transition-colors group"
              >
                <div className="font-display text-3xl text-[#3FCBA6] group-hover:text-[#1B1B3A] transition-colors w-12 flex-shrink-0">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="flex-1 pt-1">
                  <div className="font-display text-xl text-[#1B1B3A] mb-1">{item.t}</div>
                  <div className="text-sm text-[#1B1B3A]/70 font-body">{item.d}</div>
                </div>
                <Check className="w-6 h-6 text-[#1B1B3A] flex-shrink-0 mt-1.5" strokeWidth={2.5} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing CTA — Techport-style ink block */}
      <section className="ink-bg">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-24 lg:py-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 lg:col-span-9">
            <blockquote className="font-display text-4xl sm:text-5xl lg:text-7xl text-white leading-[1.05]">
              "je kan blijven praten<br />
              over <em className="italic-em" style={{ fontStyle: "normal" }}>AI op de directie-agenda</em>,<br />
              of er gewoon mee starten."
            </blockquote>
          </div>
          <div className="col-span-12 lg:col-span-3 flex flex-col justify-end gap-4">
            <Link to="/intake" className="tp-btn tp-btn--mint w-full justify-center text-base" data-testid="start-intake-button-cta">
              Ja, ik wil direct aan de slag
            </Link>
            <span className="font-condensed uppercase text-xs tracking-[0.2em] text-white/50 text-center">
              ≈ 7 min · Voor directies
            </span>
          </div>
        </div>
      </section>

      <footer className="bg-[#1B1B3A] text-white border-t border-white/10">
        <div className="max-w-[1500px] mx-auto px-6 lg:px-12 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <Brand variant="dark" />
          <div className="font-condensed uppercase text-xs tracking-[0.25em] text-white/50">
            © {new Date().getFullYear()} Techport · 100% samen, 100% slim
          </div>
        </div>
      </footer>
    </div>
  );
}
